// redux/slices/takbullCreateSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({ url, method = "GET", data, params }) => {
  const jwt = await getJwtToken();

  const config = {
    url: `${getServerUrl()}${url}`,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    withCredentials: true,
    validateStatus: () => true,
    timeout: 20000,
  };

  if (data !== undefined) config.data = data;
  if (params !== undefined) config.params = params;

  return axios(config);
};

const extractDocIds = (payload) => {
  const result = payload?.result ?? payload ?? {};

  const vm =
    result?.documentVM ||
    result?.DocumentVM ||
    result?.result?.documentVM ||
    result?.Result?.DocumentVM ||
    result;

  const uniqId =
    vm?.uniqId ||
    vm?.invoiceUniqId ||
    vm?.docUniqId ||
    vm?.documentUniqId ||
    vm?.InvUniqId ||
    null;

  const documentNumber =
    vm?.documentNumber ||
    vm?.DocumentNumber ||
    vm?.docNumber ||
    vm?.DocNumber ||
    null;

  return { uniqId, documentNumber, vm };
};

const normalizeErr = (error, fallback = "Request failed") => {
  if (!error) return fallback;

  const data = error?.response?.data || error?.data;

  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (error?.message) return error.message;

  try {
    return JSON.stringify(data ?? error);
  } catch {
    return fallback;
  }
};

const requireUid = (state) => {
  const uid =
    state?.master?.master?.uid ||
    state?.master?.uid ||
    state?.auth?.uid ||
    null;

  if (!uid) throw new Error("UID required");

  return uid;
};

const handleTakbullResponse = (res, fallback) => {
  if (res.status >= 400 || res.data?.ok === false) {
    throw {
      status: res.status,
      data: res.data,
      message:
        res.data?.error ||
        res.data?.message ||
        fallback ||
        `HTTP ${res.status}`,
    };
  }

  return res.data;
};

/* ===================== THUNKS ===================== */

export const createTakbullReceipt = createAsyncThunk(
  "takbullCreate/createReceipt",
  async ({ payload } = {}, { rejectWithValue, getState }) => {
    try {
      const uid = requireUid(getState());

      const res = await axiosRequest({
        url: "/takbull/receipt",
        method: "POST",
        data: {
          uid,
          ...(payload || {}),
        },
      });

      return handleTakbullResponse(res, "Create receipt failed");
    } catch (error) {
      return rejectWithValue(normalizeErr(error, "Create receipt failed"));
    }
  }
);

export const createTakbullCredit = createAsyncThunk(
  "takbullCreate/createCredit",
  async ({ payload } = {}, { rejectWithValue, getState }) => {
    try {
      const uid = requireUid(getState());

      const res = await axiosRequest({
        url: "/takbull/credit",
        method: "POST",
        data: {
          uid,
          ...(payload || {}),
        },
      });

      return handleTakbullResponse(res, "Create credit failed");
    } catch (error) {
      return rejectWithValue(normalizeErr(error, "Create credit failed"));
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  status: "idle",
  error: null,
  lastResult: null,
  lastUniqId: null,
  lastDocNumber: null,
};

/* ===================== SLICE ===================== */

const takbullCreateSlice = createSlice({
  name: "takbullCreate",
  initialState,
  reducers: {
    resetTakbullCreate: () => ({ ...initialState }),

    clearTakbullCreateError: (state) => {
      state.error = null;
    },

    clearTakbullCreateResult: (state) => {
      state.lastResult = null;
      state.lastUniqId = null;
      state.lastDocNumber = null;
    },

    resetTakbullCreateStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const onPending = (state) => {
      state.status = "loading";
      state.error = null;
      state.lastResult = null;
      state.lastUniqId = null;
      state.lastDocNumber = null;
    };

    const onFulfilled = (state, action) => {
      state.status = "succeeded";
      state.lastResult = action.payload?.result || action.payload || null;

      const { uniqId, documentNumber } = extractDocIds(state.lastResult);

      state.lastUniqId = uniqId || null;
      state.lastDocNumber = documentNumber || null;
    };

    const onRejected = (state, action) => {
      state.status = "failed";
      state.error =
        action.payload ||
        action.error?.message ||
        "Request failed";
    };

    builder
      .addCase(createTakbullReceipt.pending, onPending)
      .addCase(createTakbullReceipt.fulfilled, onFulfilled)
      .addCase(createTakbullReceipt.rejected, onRejected)

      .addCase(createTakbullCredit.pending, onPending)
      .addCase(createTakbullCredit.fulfilled, onFulfilled)
      .addCase(createTakbullCredit.rejected, onRejected)

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  resetTakbullCreate,
  clearTakbullCreateError,
  clearTakbullCreateResult,
  resetTakbullCreateStatus,
} = takbullCreateSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectTakbullCreate = (state) =>
  state.takbullCreate ?? state.takbull ?? initialState;

export const selectTakbullCreateStatus = (state) =>
  selectTakbullCreate(state).status || "idle";

export const selectTakbullCreateError = (state) =>
  selectTakbullCreate(state).error || null;

export const selectTakbullLastResult = (state) =>
  selectTakbullCreate(state).lastResult || null;

export const selectTakbullLastUniqId = (state) =>
  selectTakbullCreate(state).lastUniqId || null;

export const selectTakbullLastDocNumber = (state) =>
  selectTakbullCreate(state).lastDocNumber || null;

export default takbullCreateSlice.reducer;